import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, Modal, Field, Badge } from "@/components/Kit";
import { fmtDate } from "@/lib/date";
import { Plus, Trash2, BookOpen } from "lucide-react";

const EMPTY = { class_name: "", section: "A", subject: "English", title: "", description: "", due_date: "", attachment_url: "" };

export default function Homework() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data: list = [] } = useQuery({ queryKey: ["homework"], queryFn: async () => (await api.get("/school/homework")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/school/homework", form)).data,
    onSuccess: () => { toast.success("Homework assigned · Parents notified"); setOpen(false); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["homework"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/homework/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["homework"] }); },
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="homework-page">
      <PageHeader
        title="Homework Management"
        subtitle="Assign class-wise homework with attachments"
        action={<button className="ep-btn" onClick={() => setOpen(true)} data-testid="add-homework-btn"><Plus size={16} /> Assign Homework</button>}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((h) => (
          <div key={h.id} className="ep-card p-5" data-testid={`hw-card-${h.id}`}>
            <div className="mb-2 flex items-center justify-between">
              <Badge>{h.class_name}-{h.section}</Badge>
              <button onClick={() => remove.mutate(h.id)} className="text-slate-500 hover:text-rose-400"><Trash2 size={15} /></button>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen size={16} className="text-indigo-400" />
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-300">{h.subject}</p>
            </div>
            <h4 className="mt-2 font-semibold text-white">{h.title}</h4>
            <p className="mt-1 text-sm text-slate-400">{h.description}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>By {h.teacher}</span>
              {h.due_date && <span>Due {fmtDate(h.due_date)}</span>}
            </div>
          </div>
        ))}
        {!list.length && <p className="col-span-full py-8 text-center text-slate-500">No homework assigned yet</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Assign Homework" testid="add-homework-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Class" value={form.class_name} onChange={upd("class_name")} placeholder="e.g. 5" data-testid="hw-class" />
          <Field label="Section" value={form.section} onChange={upd("section")} data-testid="hw-section" />
          <label className="block col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">Subject</span>
            <select className="ep-input" value={form.subject} onChange={upd("subject")} data-testid="hw-subject">
              {["English", "Math", "Science", "Social", "Telugu", "Hindi", "Computer"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="mt-3">
          <Field label="Title" value={form.title} onChange={upd("title")} data-testid="hw-title" />
        </div>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Description</span>
          <textarea className="ep-input" rows={3} value={form.description} onChange={upd("description")} data-testid="hw-desc" />
        </label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Field label="Due Date" type="date" value={form.due_date} onChange={upd("due_date")} data-testid="hw-due" />
          <Field label="Attachment URL (PDF/Image)" value={form.attachment_url} onChange={upd("attachment_url")} data-testid="hw-attach" />
        </div>
        <button className="ep-btn mt-5 w-full" disabled={create.isPending} onClick={() => create.mutate()} data-testid="submit-homework-btn">
          {create.isPending ? "Saving…" : "Assign & Notify Parents"}
        </button>
      </Modal>
    </div>
  );
}
